import { ToneIcon } from '../primitives'
import type { NoteBlock } from '../spec/types'
import { Why } from './Why'

/**
 * A toned callout: kicker, headline, body, reference.
 *
 * Verbatim from `docs/design/reference/blocks/note.html`. The whole box is
 * tinted by the tone and outlined in `currentColor`, so a critical note is
 * unmistakable without shouting in a second colour.
 *
 * Notes carry the load in this console. Where a lesser design would leave a
 * screen to be interpreted, the corpus uses 37 of these to say the quiet part
 * — which invariant is at stake, why an empty queue is the good news, why a
 * figure is stale rather than wrong. The `ref` line is the evidence: a table
 * name, a case id, an elapsed time.
 *
 * # Why a note may start collapsed, and why almost none of them do
 *
 * There are **359 of these in the adapters** and on a dense screen they are
 * most of what an operator reads before reaching a value. So a body may start
 * collapsed — but ONLY when the adapter marked it `quiet`, and only then.
 *
 * There is no rule here that infers it. A rule by tone would have been right
 * about most and silently wrong about the rest; a rule by length or by wording
 * would be a regex deciding whether a warning is visible. Both fail in the
 * same direction, which is the expensive one: **erring open costs density,
 * erring closed costs truth.**
 *
 * # Two things this component refuses even when asked
 *
 * **A toned note never collapses.** `a`, `r`, `g` and `v` render exactly as
 * they always have, whatever `quiet` says. A warning is not reference material
 * — it is the thing the screen exists to say — and *"unconfirmed — NOT clear"*
 * or *"an empty queue does NOT mean the book is clean"* behind a click would
 * make a screen look calm while being wrong by omission. Enforcing it here
 * rather than trusting the caller means a mis-marked warning is a no-op instead
 * of an invisible defect.
 *
 * **The kicker, the title and the ref never collapse.** Only the body does. So
 * a collapsed note still states its headline and still shows its evidence; what
 * moves is the paragraph arguing for it.
 */
export function Note({ block }: { block: NoteBlock }) {
  return (
    <div
      data-tone={block.tone ?? 'n'}
      style={{ display: 'flex', gap: '12px', padding: '10px 12px', border: '1px solid currentColor' }}
    >
      <span
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontWeight: 800,
          fontSize: '9.5px',
          letterSpacing: '.07em',
          textTransform: 'uppercase',
          paddingTop: '1px',
        }}
      >
        <ToneIcon tone={block.tone} size={13} />
        <span>{block.kicker ?? 'Note'}</span>
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '12.5px' }}>{block.title}</div>
        {/* Only an explicitly quiet, UNTONED note collapses. The tone check is
            here rather than at the call sites so that mis-marking a warning is
            a no-op, not an invisible defect. */}
        {block.quiet === true && (block.tone === undefined || block.tone === 'n')
          ? <Why>{block.body}</Why>
          : (
              <div
                style={{ fontSize: '12px', marginTop: '2px', maxWidth: '82ch', textWrap: 'pretty' }}
              >
                {block.body}
              </div>
            )}
        <div
          style={{
            fontFamily: 'var(--k-mono)',
            fontSize: '11px',
            marginTop: '4px',
            opacity: 0.8,
          }}
        >
          {block.ref}
        </div>
      </div>
    </div>
  )
}
